import styled from 'styled-components';

import Alert from '../../components/Alert';
import ArticleCard from '../../components/ArticleCard';
import Link from '../../components/Link';
import { RELEASE_URL } from '../../config/env';
import {
  Button,
  IconArrowRight as ArrowIcon,
  IconChevronRight as ChevronRightIcon,
} from '../../core';
import abcImage from '../../images/abc-image.png';
import chartDownImg from '../../images/chart-down.png';
import dottedBg from '../../images/dotted-bg.jpg';
import overviewHeroImage from '../../images/overview-hero.png';
import searchChartImg from '../../images/search-chart.png';

interface Props {
  className?: string;
}

const Overview = ({ className = '' }: Props) => (
  <div className={className}>
    <section className="hero">
      <Alert
        className="alert"
        iconName="gear"
        mainText="Design system alert:"
        additionalText="New chart types added to the builder."
        actions={<Link href="/data-visualization">Learn More</Link>}
      />
      <div className="hero__inner">
        <div className="hero__left">
          <h1 data-text="Unleash Your Data's Potential">Unleash Your Data&apos;s Potential</h1>
          <p>
            Unleash your data&apos;s potential with the help of the DHCS Data Visualization Builder.
            Effortlessly create interactive visualizations and narrate compelling stories with your
            data in minutes. No technical expertise required – simply select your data, customize
            your visuals, and share your insights.
            <br /> Transform your data into impactful visuals with ease.
          </p>
          <div className="hero__footer">
            <Button
              onClick={() => (window.location.href = '/design-system/chart-match')}
              variant="subtle"
              iconAfter={<ArrowIcon />}
              size="M"
            >
              Learn More
            </Button>
            <Button
              onClick={() => (window.location.href = RELEASE_URL)}
              variant="primary"
              size="M"
              iconAfter={<ChevronRightIcon />}
            >
              Try the Builder
            </Button>
          </div>
        </div>
        <div className="hero__right" />
      </div>
    </section>
    <section className="cards">
      <ArticleCard
        title="Select the Perfect Visualization"
        content="With so many visualization option,
                   it can be hard to know where to start, and
                   which chart to use.  Our powerful and intuitive
                    ChartMatch can help you decide."
        imagePath={chartDownImg}
        linkPath="/design-system/chart-match"
      />
      <ArticleCard
        title="Crisp, Clean Design Every Time"
        content="DHCS's custom design system ensures consistency, efficiency, and scalability across projects while empowering designers to prioritize creativity and innovation."
        imagePath={searchChartImg}
        linkPath="/"
      />

      <ArticleCard
        className="last-card"
        imagePath={abcImage}
        title="Build Custom Components"
        content="Whether you're working with PowerBI, Excel, ESRI, or Figma, if you need to build it, we've got you covered. For more information, explore our comprehensive Dev Resources documentation."
        linkPath="/dev-resources"
      />
    </section>
  </div>
);

const OverviewPage = styled(Overview)`
  margin: 0 auto;
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: flex;
    flex-direction: column;
    flex: 1;
  }
  > .hero {
    background-image: url(${dottedBg});
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    padding-top: 24px;
    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      padding-top: 16px;
      display: flex;
      flex-direction: column;
      flex: 1;
    }

    > .hero__inner {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 0 48px;
      flex-wrap: wrap;
      flex-direction: column-reverse;
      padding-bottom: 72px;
      padding-top: 48px;

      @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
        flex-direction: row;
        flex-wrap: nowrap;
        padding-top: 14px;
        padding-bottom: 48px;
      }
      > .hero__left {
        flex-basis: 50%;
        padding-left: 16px;
        padding-right: 16px;
        @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
          padding-left: 48px;
          padding-right: 0;
        }
        @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
          flex-basis: 35%;
          flex-shrink: 0;
        }
        > h1 {
          font-weight: 600;
          letter-spacing: ${({ theme }) => theme.typography.display2.letterSpacing};
          font-size: ${(props) => props.theme.fontSizes.xl};
          line-height: ${(props) => props.theme.lineHeights.xl};
          position: relative;
          background: linear-gradient(
            to left,
            /* tokens-allow: pending design decision */ #155676,
            ${({ theme }) => theme.primaryBackgroundStrong}
          );
          color: transparent;
          background-clip: text;
          margin-bottom: 16px;

          @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
            font-size: ${(props) => props.theme.fontSizes['2xl']};
            line-height: ${(props) => props.theme.lineHeights['2xl']};
          }

          &::after {
            content: attr(data-text);
            font-size: ${(props) => props.theme.fontSizes['2xl']};
            line-height: ${(props) => props.theme.lineHeights['2xl']};
            width: 100%;
            position: absolute;
            left: 0;
            top: 0;
            color: transparent;
            -webkit-text-stroke: 1px #383d43; /* tokens-allow: pending design decision */
            -webkit-text-fill-color: transparent;
            z-index: -1;
          }
        }

        > p {
          font-size: ${(props) => props.theme.fontSizes.base};
          line-height: ${(props) => props.theme.lineHeights.base};
          font-weight: 400;
          color: ${(props) => props.theme.neutralTextWeak};
          margin-bottom: 24px;
        }

        > .hero__footer {
          display: flex;
          gap: 24px;
        }
      }
      > .hero__right {
        background-image: url(${overviewHeroImage});
        background-size: contain;
        background-position: center;
        background-repeat: no-repeat;
        width: 100%;
        height: 200px;
        margin-bottom: 32px;

        @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
          flex-basis: 65%;
          background-position: center right;
          height: 440px;
          padding-right: 48px;
          margin-bottom: 0;
        }

        @media (min-width: ${({ theme }) => theme.breakpoints['2xl']}) {
          height: 100%;
        }
      }
    }
  }

  > .cards {
    background: linear-gradient(
        to top,
        ${({ theme }) => theme.neutralBackgroundBase},
        ${({ theme }) => theme.neutralBackgroundTransparent}
      ),
      ${({ theme }) => theme.neutralBackgroundMedium};
    @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
      display: flex;
      flex-direction: row;
      margin-top: auto;
    }

    .last-card {
      padding-bottom: 64px;
      border-right: 0;
      @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
        padding-bottom: 24px;
      }
    }
  }
`;

export default OverviewPage;
